import React, { useState } from 'react'

import { ExclusiveRewardsSection } from '@/components/ExclusiveRewardsSection'
import { useAppSelector } from '@/redux/hooks/hooks'
import { useFeatureSelector } from '@vrw/data/src/redux/features/features.selectors'
import { FeatureName } from '@vrw/data/src/redux/features/features.types'
import { CurvedContainer } from '@/components/CurvedContainer'
import {
  MobileAppsSplash,
  RewardsFilterWrapper,
  RewardsListPrivateEarn,
  RewardsListPublicEarn,
  RewardsListPublicSpend,
  RewardsListPrivateSpend,
  Content,
} from '@/components'
import { PillInstanceType, Spacer, styled, View, XStack } from '@red-ui/components'
import { color } from '@/style/variables'
import { DiscoveryHeader } from './DiscoveryHeader'
import { CategoriesResponse } from '@vrw/data/src/redux/categories/types'
import { useCategories } from '@/query/rewards/useCategories'
import { useRewards } from '@/query/rewards/useRewards'
import { RewardsSort as RewardsSortType } from '@vrw/data/src/redux/discovery/types'
import { RewardsSort } from './Sort/Sort'
import { RewardsList } from '../RewardsList/RewardsList'
import { getIsAuthenticated } from '@vrw/data/src/redux/auth/selectors'

interface DiscoveryFeedProps {
  title: string
  isEarn: boolean
}

const PinkBackground = styled(View, {
  height: '210px',
  marginTop: '130px',
  marginBottom: '-350px',
  backgroundImage: 'linear-gradient(to bottom, #fce6e6, #fef4f4 57%, #ffffff)',
  $gtMobile: {
    height: '200px',
    marginBottom: '-330px',
  },
  $gtDesktop: {
    height: '200px',
    marginBottom: '-350px',
  },
})

const ALL_KEY = 'all'

const transformCategoriesObject = (categoriesData: CategoriesResponse) => {
  let categories = [] as PillInstanceType['pillItem'][]
  if (categoriesData.categories && categoriesData.categoryCostCounts) {
    categories = [
      { name: 'All', count: categoriesData.categoryCostCounts[ALL_KEY][ALL_KEY] ?? 0 },
      ...categoriesData.categories.map((category) => ({
        name: category.categoryName,
        count: categoriesData.categoryCostCounts?.[category.categoryName]?.[ALL_KEY] ?? 0,
      })),
    ]
  }
  return categories
}

export const DiscoveryFeed = ({ title, isEarn }: DiscoveryFeedProps) => {
  const [sort, setSort] = useState<RewardsSortType | undefined>(undefined)
  const newFiltersFlag = useAppSelector(useFeatureSelector(FeatureName.NEW_FILTERS))
  const isAuthenticated = useAppSelector(getIsAuthenticated)
  const { data: rewards, isLoading } = useRewards(isEarn, [], sort)
  const { data: categories } = useCategories(isEarn)

  const renderDeprecatedRewardList = () => {
    if (isEarn) {
      return isAuthenticated ? <RewardsListPrivateEarn /> : <RewardsListPublicEarn />
    }
    return isAuthenticated ? <RewardsListPrivateSpend /> : <RewardsListPublicSpend />
  }

  return (
    <>
      {newFiltersFlag ? (
        <>
          <DiscoveryHeader title={title} categories={transformCategoriesObject(categories)} />
          {isAuthenticated && <ExclusiveRewardsSection />}
          <Content>
            {isEarn ? (
              <Spacer size="$5" />
            ) : (
              <XStack marginVertical="$2" justifyContent="flex-end">
                <RewardsSort sort={sort} setSort={setSort} />
              </XStack>
            )}
          </Content>
          <RewardsList rewards={rewards} isLoading={isLoading} />
        </>
      ) : (
        <>
          <CurvedContainer position="bottom" height={65} offset={140} overlayColor={color.darkPinkBackground}>
            {isAuthenticated && <ExclusiveRewardsSection />}
            <RewardsFilterWrapper isEarn={isEarn} isPublic={!isAuthenticated} />
          </CurvedContainer>
          <PinkBackground />
          {renderDeprecatedRewardList()}
        </>
      )}
      <MobileAppsSplash />
    </>
  )
}
